import { SmsDisclosure } from '@Auth/components/SmsDisclosure';
import { useForm } from '@inertiajs/react';
import { FormEventHandler, useId } from 'react';

import { Button } from '@/components/ui/Button';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { FormCheckboxInput } from '@/components/ui/form/FormCheckboxInput';
import { cn } from '@/lib/utils';

type SmsConsentPromptProps = {
  maskedPhone: string;
  notice: Diagonal.Auth.Data.SmsConsentNoticeData;
  agreed: boolean;
  onAgreedChange: (agreed: boolean) => void;
  onSubmit: () => void;
  processing: boolean;
  errors: string[];
  buttonClassName?: string;
};

/**
 * The SMS consent notice that every surface shows before the first code goes to a phone
 * number. The wording, the unchecked checkbox, and the disclosure are fixed: the stored
 * consent records `notice.version`, which names exactly this text.
 */
export function SmsConsentPrompt({
  maskedPhone,
  notice,
  agreed,
  onAgreedChange,
  onSubmit,
  processing,
  errors,
  buttonClassName
}: SmsConsentPromptProps) {
  const checkboxId = useId();

  const submit: FormEventHandler = (event) => {
    event.preventDefault();
    event.stopPropagation();

    onSubmit();
  };

  return (
    <form onSubmit={submit} className="grid gap-3">
      <p className="text-sm">
        We will text a 6-digit verification code to the phone number ending {maskedPhone}.
      </p>
      <FormCheckboxInput
        id={checkboxId}
        label="I agree to receive verification codes by text message at this number."
        checked={agreed}
        onCheckedChange={(checked) => onAgreedChange(checked === true)}
      />
      <SmsDisclosure notice={notice} />
      <Button
        type="submit"
        className={cn('w-full', buttonClassName)}
        disabled={processing || !agreed}
      >
        Text me a code
      </Button>
      <ErrorMessage errors={errors} />
    </form>
  );
}

type SmsConsentFormProps = {
  url: string;
  operation?: 'send' | 'send-current';
  maskedPhone: string;
  notice: Diagonal.Auth.Data.SmsConsentNoticeData;
};

/**
 * Asks for affirmative consent before the first code goes to a phone number. The
 * server records the consent with the notice version shown here, then sends the code.
 */
export function SmsConsentForm({ url, maskedPhone, notice, operation }: SmsConsentFormProps) {
  const { data, setData, post, processing, errors } = useForm({
    ...(operation ? { operation } : {}),
    sms_consent: false,
    notice_version: notice.version,
    consent_token: notice.consent_token
  });

  return (
    <SmsConsentPrompt
      maskedPhone={maskedPhone}
      notice={notice}
      agreed={data.sms_consent}
      onAgreedChange={(agreed) => setData('sms_consent', agreed)}
      onSubmit={() => post(url, { preserveScroll: true })}
      processing={processing}
      errors={Object.values(errors).flat()}
    />
  );
}
